import React from 'react';
import {
  FormControl,
  TableContainer,
  Table,
  TableCell,
  TableHead,
  Grid,
  TableRow,
  Paper,
  TableBody,
  Radio,
  Typography,
  Button,
  Modal,
  Box
} from '@mui/material';
import { Link } from 'react-router-dom';

import { withoutOverlay } from '../components/OverlayWithButton';
import { useSubscriptionStore } from '../data';
import { useApiRepo, Endpoints } from '../utils/Api';
import { Colors } from '../utils/Colors';

const enum SURVEY_TYPES {
  GAD7 = 'gad7'
}

const gad7 = [
  {
    order: 1,
    question: 'Feeling nervous, anxious, or on edge.',
    responses: [
      'Not at All',
      'Several Days',
      'More than Half the Days',
      'Nearly Every Day'
    ],
    scores: [0, 1, 2, 3]
  },
  {
    order: 2,
    question: 'Not being able to stop or control worrying.',
    responses: [
      'Not at All',
      'Several Days',
      'More than Half the Days',
      'Nearly Every Day'
    ],
    scores: [0, 1, 2, 3]
  },
  {
    order: 3,
    question: 'Worrying too much about different things.',
    responses: [
      'Not at All',
      'Several Days',
      'More than Half the Days',
      'Nearly Every Day'
    ],
    scores: [0, 1, 2, 3]
  },
  {
    order: 4,
    question: 'Trouble relaxing.',
    responses: [
      'Not at All',
      'Several Days',
      'More than Half the Days',
      'Nearly Every Day'
    ],
    scores: [0, 1, 2, 3]
  },
  {
    order: 5,
    question: 'Being so restless that it is hard to sit still.',
    responses: [
      'Not at All',
      'Several Days',
      'More than Half the Days',
      'Nearly Every Day'
    ],
    scores: [0, 1, 2, 3]
  },
  {
    order: 6,
    question: 'Becoming easily annoyed or irritable.',
    responses: [
      'Not at All',
      'Several Days',
      'More than Half the Days',
      'Nearly Every Day'
    ],
    scores: [0, 1, 2, 3]
  },
  {
    order: 7,
    question: 'Feeling afraid as if something awful might happen.',
    responses: [
      'Not at All',
      'Several Days',
      'More than Half the Days',
      'Nearly Every Day'
    ],
    scores: [0, 1, 2, 3]
  }
];
const style = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4
};

export function AnxietySurvey() {
  const { status } = useSubscriptionStore();
  const defaultArray = Array(gad7.length).fill(null);
  const [submission, setSubmission] =
    React.useState<(number | null)[]>(defaultArray);

  // Modal
  const [open, setOpen] = React.useState(false);
  const modalOpen = () => setOpen(true);
  const modalClose = () => setOpen(false);

  // const { getObjectsFromAPI } = useApiRepo();
  function validResults() {
    return !submission.some((i) => i === null);
  }
  function getResults() {
    if (validResults()) {
      const score = (submission as number[]).reduce((sum, val) => sum + val);
      return score;
    }
    return -1;
  }

  const { getObjectsFromAPI } = useApiRepo();

  async function submitSurvey() {
    const results = getResults();
    if (results === -1) {
      return;
    }
    modalOpen();
    const responses = { survey: gad7, results: submission };
    const postBody = {
      survey_type: SURVEY_TYPES.GAD7,
      responses,
      score: results
    };
    const api = await getObjectsFromAPI();
    await api.post(Endpoints.surverys, postBody);
  }

  function handleForm(e: any, question: number) {
    const selectedValue = parseInt(e.target.value, 10);
    const copySubmission = [...submission];
    copySubmission[question - 1] = selectedValue;
    setSubmission(copySubmission);
  }

  return (
    <Grid container direction="row" spacing={2} style={{ padding: '40px' }}>
      <Modal
        open={open}
        onClose={modalClose}
        aria-labelledby="anxiety-score-results"
      >
        <Box sx={style}>
          <Typography id="modal-modal-title" variant="h6" component="h2">
            Your Anxiety Score is: {getResults()}
          </Typography>
          <br />
          <Typography variant="h6" component="h2">
            Scoring:
          </Typography>
          <Typography variant="h6" component="h2">
            5 to 9 = mild anxiety
          </Typography>
          <Typography variant="h6" component="h2">
            10 to 14 = moderate anxiety
          </Typography>
          <Typography variant="h6" component="h2">
            15 to 21 = severe anxiety
          </Typography>
          <Link to="/dashboard">
            <Button
              fullWidth
              variant="contained"
              style={{
                backgroundColor: Colors.ocdGreen,
                color: 'black',
                height: '50px',
                borderRadius: '20px'
              }}
            >
              Okay
            </Button>
          </Link>
        </Box>
      </Modal>
      <Grid item xs={12}>
        <Typography variant="h3">Anxiety Survey</Typography>
        <Typography variant="h5">
          To submit an anxiety survey, answer all questions and click &#39;Get
          Results.&#39;
        </Typography>
      </Grid>
      {withoutOverlay.includes(status) ? (
        <>
          <Grid item xs={12}>
            <FormControl style={{ width: '100%' }}>
              <TableContainer component={Paper}>
                <Table aria-label="simple table">
                  <TableHead>
                    <TableRow>
                      <TableCell>{}</TableCell>
                      {gad7[0].responses.map((response) => (
                        <TableCell key={response} align="center">
                          {response}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {gad7.map((row) => (
                      <TableRow
                        key={row.question}
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 }
                        }}
                      >
                        <TableCell component="th" scope="row">
                          {row.question}
                        </TableCell>

                        {row.scores.map((score) => (
                          <TableCell key={score} align="center">
                            <Radio
                              checked={submission[row.order - 1] === score}
                              onChange={(event) => handleForm(event, row.order)}
                              value={score}
                              name="radio-buttons"
                              inputProps={{ 'aria-label': String(score) }}
                            />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Button
              fullWidth
              disableElevation
              variant="contained"
              style={{ backgroundColor: Colors.lightGray, color: 'black' }}
              onClick={submitSurvey}
            >
              Get Results
            </Button>
          </Grid>
        </>
      ) : null}
    </Grid>
  );
}
